import React from "react";
import axios from "axios";
import { useLocation, useParams, useNavigate } from "react-router-dom";

export const base_url = process.env.REACT_APP_BASE_URL;
export const api_type  = process.env.REACT_APP_API_TYPE;

/*
  Expected FAQ item shape:
  {
    id: number (optional),
    question: "First?",
    answer: "Yes",
    status: true,
    isStared: false
  }
*/

export default function FaqListByCategory() {
  const { id } = useParams(); // category id
  const navigate = useNavigate();
  const location = useLocation();
  const categoryTitle = location?.state?.title;

  const [rows, setRows] = React.useState([]); // FAQ array
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const authHeader = React.useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(
        `${base_url}/${api_type}/cms/partner/faqCategory/${id}/faqs`,
        { headers: authHeader }
      );
      const items = data?.data?.items ?? data?.data ?? data ?? [];
      setRows(Array.isArray(items) ? items : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  }, [id, authHeader]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="bg-white p-4 rounded-lg mb-4 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[#252C32] text-[22px] font-bold">
              FAQs {categoryTitle ? `— ${categoryTitle}` : ""}
            </h1>
            <p className="text-sm text-gray-500">Category ID: {id}</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>

      <div className="overflow-x-auto w-full">
        {error && (
          <div className="mx-5 my-2 px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}
        {loading ? (
          <div className="p-5 text-sm text-gray-600">Loading…</div>
        ) : (
          <div className="overflow-hidden border rounded-2xl shadow-sm bg-white">
            <div className="px-4 py-3 border-b bg-gray-50/60">
              <h2 className="text-base font-semibold text-gray-900">FAQs</h2>
            </div>

            <table className="min-w-full table-fixed">
              <colgroup>
                <col style={{ width: "35%" }} />
                <col style={{ width: "35%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-3 border-b">Question</th>
                  <th className="text-left p-3 border-b">Answer</th>
                  <th className="text-center p-3 border-b">Status</th>
                  <th className="text-center p-3 border-b">Starred</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((r) => (
                  <tr key={r.id ?? r.question} className="odd:bg-white even:bg-gray-50">
                    <td className="p-3 border-b align-top">{r.question || "—"}</td>
                    <td className="p-3 border-b align-top">{r.answer || "—"}</td>
                    <td className="p-3 border-b align-top text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${r.status ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                        {r.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-3 border-b align-top text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${r.isStared ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"}`}>
                        {r.isStared ? "Starred" : "—"}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td className="p-4 text-sm text-gray-500" colSpan={4}>
                      No FAQs yet for this category.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
